import React from "react";
import { useShopfrontDispatch } from "../../../_Bootstrap/hooks";
import { revokeAudioTrack } from "../../../Actions/Audio/AudioActions";
import Button from "../../../Common/Buttons/Button";
import Range from "../../../Common/Forms/Range";
import AudioManager, {
    type AudioTrack,
    AudioTrackEvents,
    type AudioTrackId,
} from "../../../Utilities/Platform/Audio/AudioManager";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";

interface Props {
    id?: string;
    name: string;
    track: AudioTrack | AudioTrackId;
}

const AudioControl: React.FunctionComponent<Props> = ({ track: trackInput, name, id }) => {
    const dispatch = useShopfrontDispatch();

    const track = React.useMemo(() => {
        if(typeof trackInput === "string") {
            return AudioManager.getInstance().getTrack(trackInput);
        }

        return trackInput;
    }, [ trackInput ]);

    const [ mute, setMute ] = React.useState(track.isMuted());
    const [ volume, setVolume ] = React.useState(() => {
        return Math.round(track.getLevel() * 100);
    });

    const onToggleMute = () => {
        track.toggleMute();
    };

    const onSetVolume = (value: number) => {
        track.setVolume(value / 100);
    };

    const onRevoke = () => {
        if(typeof id === "string") {
            AudioManager.getInstance().removeTrack(track);
            dispatch(revokeAudioTrack(id));
        }
    };

    React.useEffect(() => {
        const listener = () => {
            setMute(track.isMuted());
            setVolume(Math.round(track.getLevel() * 100));
        };

        track.addListener(AudioTrackEvents.update, listener);

        return () => {
            track.removeListener(AudioTrackEvents.update, listener);
        };
    }, [ track ]);

    const percentage = (mute ? "Muted" : `${volume}%`);

    return (
        <div className="audio-control">
            <div className="audio-name-wrapper">
                <div className="audio-name">{name}</div>
                <div className="audio-percentage"> - {percentage}</div>
            </div>
            <div className="audio-volume">
                <div className="audio-volume-icon">
                    <FontAwesomeLight icon="volume-down" />
                </div>
                <Range
                    min={0}
                    max={100}
                    value={volume}
                    disabled={mute}
                    onChange={onSetVolume}
                />
                <div className="audio-volume-icon">
                    <FontAwesomeLight icon="volume-up" />
                </div>
            </div>
            <div className="audio-actions">
                <Button onClick={onToggleMute}>
                    {mute ? "Unmute" : "Mute"}
                </Button>
                {typeof id === "string" && (
                    <Button onClick={onRevoke}>
                        Revoke
                    </Button>
                )}
            </div>
        </div>
    );
};

export default AudioControl;
